import { Component, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { MatIconModule } from '@angular/material/icon';
import { RenderRulesComponent } from '../../../../common/render-rules/render-rules.component';
import { SubSectionService } from '../../../../services/builder/sub-section.service';
import { UniqueIdService } from '../../../../services/builder/unique-id.service';
import { DialogModule } from 'primeng/dialog';
import { RulesService } from '../../../../services/builder/rules.service';
import { ChooseShowIfCompletionComponent } from '../../../new-custom-panel/choose-showif.component';
import { TabsModule } from 'primeng/tabs';
import { ConcerningAreaType } from '../../../../api/interfaces';
import { SelectModule } from 'primeng/select';

@Component({
  selector: 'lib-subsection-customization-panel',
  template: `
    <p-dialog
      header="Personalizza sottosezione"
      [appendTo]="'body'"
      [modal]="modal()"
      [closable]="closable()"
      [draggable]="false"
      [closeOnEscape]="true"
      [visible]="subSectionService.subsectionCustomizationVisible()"
      (visibleChange)="updateVisiblity($event)"
      [appendTo]="'body'"
      [position]="position()"
    >
      @if (!rulesService.selectionActive()) {
        <div
          [formGroup]="customizationForm"
          class="flex h-full w-full flex-col gap-4 overflow-hidden"
        >
          <p-tabs [value]="0">
            <p-tablist>
              <p-tab [value]="0">Impostazioni base</p-tab>
              <p-tab [value]="1">Impostazioni avanzate</p-tab>
            </p-tablist>
            <p-tabpanels>
              <p-tabpanel [value]="0" class="overflow-hidden">
                <div class="tab-panel h-120 flex flex-col gap-4">
                  <div class="flex flex-row gap-2">
                    <div class="flex w-full flex-col">
                      <label for="desc">Titolo</label>
                      <input id="desc" pInputText formControlName="title" />
                    </div>
                  </div>
                  <div class="flex flex-row gap-2">
                    <div class="flex w-full flex-col">
                      <label for="desc">Sottotitolo</label>
                      <input id="desc" pInputText formControlName="subTitle" />
                    </div>
                  </div>
                  <div class="flex flex-row gap-2">
                    <div class="flex w-full flex-col">
                      <label for="desc">Descrizione</label>
                      <input
                        id="desc"
                        pInputText
                        formControlName="description"
                      />
                    </div>
                  </div>
                  <div class="flex flex-col gap-2">
                    <div class="flex w-full flex-col">
                      <label for="points"
                        >Punti ottenibili nella sottosezione</label
                      >
                      <p-inputnumber
                        [autofocus]="false"
                        formControlName="points"
                      ></p-inputnumber>
                    </div>
                    @if (customizationForm.controls.points.hasError('min')) {
                      <div class="text-red-500">
                        I punti non possono essere negativi
                      </div>
                    }
                    @if (
                      customizationForm.controls.points.hasError('exceedPoints')
                    ) {
                      <div class="text-red-500">
                        I punti superano il massimo consentito dalla sezione ({{
                          subSectionService.sectionPoints()
                        }})
                      </div>
                    }
                  </div>
                  <div class="flex flex-col gap-2">
                    <div class="flex w-full flex-col">
                      <label for="points">Area della sottosezione</label>
                      <p-select
                        [autofocus]="false"
                        formControlName="concerningArea"
                        [fluid]="true"
                        [appendTo]="'body'"
                        [options]="areas"
                        optionLabel="label"
                        optionValue="value"
                      ></p-select>
                    </div>
                  </div>
                </div>
              </p-tabpanel>
              <p-tabpanel [value]="1" class="overflow-hidden">
                <div class="tab-panel h-120 flex flex-col gap-4">
                  <div class="flex flex-col">
                    <label for="id">Scegli ID</label>
                    <input
                      id="id"
                      type="text"
                      pInputText
                      formControlName="id"
                      pKeyFilter="alphanum"
                    />
                    @if (
                      customizationForm.controls.id.hasError('forbiddenId')
                    ) {
                      <div class="text-red-500">Hai già usato questo alias</div>
                    }
                    @if (customizationForm.controls.id.hasError('required')) {
                      <div class="text-red-500">Il campo è obbligatorio</div>
                    }
                  </div>
                  <lib-render-rules
                    [form]="customizationForm"
                    [showPickButton]="true"
                    (openedShowIf)="openShowIfSelection()"
                  />
                </div>
              </p-tabpanel>
            </p-tabpanels>
          </p-tabs>
          <div class="footer-bar flex w-full flex-row justify-end gap-2">
            <p-button
              (click)="updateSubSection(false)"
              [fluid]="true"
              [text]="true"
              class="w-30"
            >
              Annulla
            </p-button>
            <p-button
              (click)="updateSubSection(true)"
              cdkFocusInitial
              [fluid]="true"
              class="w-30"
            >
              Conferma
            </p-button>
          </div>
        </div>
      } @else {
        <lib-choose-showif-completion-component
          [codeMirrorControl]="customizationForm.controls.showIfRule"
          (hide)="closeSelector()"
        />
      }
    </p-dialog>
  `,
  imports: [
    SelectModule,
    RenderRulesComponent,
    FormsModule,
    TabsModule,
    ReactiveFormsModule,
    InputTextModule,
    InputNumberModule,
    ButtonModule,
    MatIconModule,
    DialogModule,
    ChooseShowIfCompletionComponent,
  ],
})
export class SubSectionCustomizationPanelComponent {
  private readonly fb = inject(FormBuilder);
  private readonly uniqueIdService = inject(UniqueIdService);
  protected readonly subSectionService = inject(SubSectionService);
  protected readonly rulesService = inject(RulesService);

  protected modal = signal(true);
  protected closable = signal(false);
  protected position = signal<
    | 'center'
    | 'left'
    | 'top'
    | 'bottom'
    | 'right'
    | 'topleft'
    | 'topright'
    | 'bottomleft'
    | 'bottomright'
  >('center');

  customizationForm = this.fb.group({
    id: [
      '',
      [
        Validators.required,
        this.uniqueIdService.forbiddenIdValidator(
          this.subSectionService.uniqueId ?? '',
        ),
        this.uniqueIdService.isEmptySpace(),
      ],
    ],
    title: [''],
    subTitle: [''],
    description: [''],
    points: [
      0,
      [
        this.pointValidator(
          this.subSectionService.subsection()?.customization.points ?? 0,
          this.subSectionService.sectionPoints(),
          this.subSectionService.sectionUsedPoints(),
        ),
        Validators.min(0),
      ],
    ],
    concerningArea: [null] as [ConcerningAreaType | null],
    showIfRule: ['', [this.uniqueIdService.idNotExistsValidator()]],
  });

  readonly areas: { label: string; value: ConcerningAreaType }[] = [
    { label: 'Riequilibrio', value: 'REQUALIFICATION' },
    {
      label: 'Accessibilità e qualità',
      value: 'ACCESSIBILITY_N_QUALITY',
    },
    { label: 'Sicurezza e salvamento', value: 'SAFETY_N_LIFEGUARD' },
    { label: 'Esperienza e gestione', value: 'EXPERIENCE_N_MANAGEMENT' },
    {
      label: 'Sostenibilità e ambiente',
      value: 'SUSTAINABILITY_N_ENVIRONMENT',
    },
  ];

  constructor() {
    const subSection = this.subSectionService.subsection();
    if (!subSection) return;

    const uniqueId = this.subSectionService.uniqueId;
    if (!uniqueId) return;

    this.customizationForm.patchValue({
      id: uniqueId,
      title: subSection.customization.title,
      subTitle: subSection.customization.subTitle,
      description: subSection.customization.description,
      showIfRule: subSection.customization.showIfRule ?? '',
      points: subSection.customization.points || 0,
      concerningArea: subSection.customization.concerningArea || null,
    });
  }

  updateSubSection(success: boolean) {
    if (!success) {
      this.subSectionService.subsectionCustomizationVisible.set(false);
      return;
    }

    const formValue = this.customizationForm.value;
    const id = formValue.id;

    if (!id) return;

    this.subSectionService.updateSubSection(id, {
      title: formValue.title ?? '',
      subTitle: formValue.subTitle ?? '',
      description: formValue.description ?? '',
      showIfRule: formValue.showIfRule ?? '',
      points: formValue.points ?? 0,
      concerningArea: formValue.concerningArea ?? null,
    });
    this.subSectionService.subsectionCustomizationVisible.set(false);
  }

  updateVisiblity(visible: boolean) {
    if (!visible)
      this.subSectionService.subsectionCustomizationVisible.set(visible);
  }

  openShowIfSelection() {
    this.rulesService.selectionActive.set(true);
    this.modal.set(false);
    this.closable.set(false);
    this.position.set('top');
  }

  closeSelector() {
    this.rulesService.selectionActive.set(false);
    this.modal.set(true);
    this.closable.set(true);
    this.position.set('center');
  }

  private pointValidator(
    currentSubsectionPoints: number,
    pointsToNotExceed: number,
    currentPointsUsed: number,
  ): ValidatorFn {
    return (control) => {
      const value = control.value;

      if (
        value + (currentPointsUsed - currentSubsectionPoints) >
        pointsToNotExceed
      ) {
        return { exceedPoints: true };
      }

      return null;
    };
  }
}
